import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { getApplicationForm, saveApplicationForm } from '../services/applicationService';

// ─── Constants ───────────────────────────────────────────────────────────────
const EMPLOYMENT_OPTS = ['Salaried', 'Self Employed', 'Internship', 'Freelance', 'Currently Studying', 'Other'];

const SECTIONS = [
  { id: 's1', label: 'Student Details',     short: 'Student'    },
  { id: 's2', label: 'University Details',  short: 'University' },
  { id: 's3', label: 'Parents Details',     short: 'Parents'    },
  { id: 's4', label: 'Co-Applicant',        short: 'Co-Applicant'},
  { id: 's5', label: 'Miscellaneous',       short: 'Misc'       },
];

const EMPTY = {
  s1: { maritalStatus:'', currentAddress:'', currentAddressYears:'', permanentAddress:'',
        tenthScore:'', twelfthScore:'', bachelorsScore:'', graduationBacklogs:'',
        languageTest:'', greGmatScore:'', refPhone:'', refAddress:'',
        employmentType:'', workExperience:'', companyAddress:'' },
  s2: { targetCountry:'', universityName:'', courseName:'', courseDuration:'', loanAmount:'' },
  s3: { fatherName:'', fatherPhone:'', fatherEmail:'',
        motherName:'', motherPhone:'', motherEmail:'', residenceAddress:'' },
  s4: { name:'', phone:'', email:'', address:'', relationship:'', occupation:'',
        officeAddress:'', yearsInJob:'', annualIncome:'', monthlyEmis:'',
        fatherName:'', motherName:'',
        ref1Name:'', ref1Phone:'', ref1Email:'', ref1Address:'',
        ref2Name:'', ref2Phone:'', ref2Email:'', ref2Address:'',
        hasAdditional:'' },
  s5: { previousBanks:'', ownHouse:'', collateral:'', collateralTypes:[] },
};

// ─── Small helpers ────────────────────────────────────────────────────────────
function Field({ label, required, children }) {
  return (
    <div>
      <label className="block text-xs font-semibold text-slate-600 mb-1.5">
        {label}{required && <span className="text-red-500 ml-0.5">*</span>}
      </label>
      {children}
    </div>
  );
}

function TextInput({ value, onChange, placeholder, type = 'text' }) {
  return (
    <input
      type={type}
      value={value}
      onChange={e => onChange(e.target.value)}
      placeholder={placeholder}
      className="input"
    />
  );
}

function TextArea({ value, onChange, placeholder, rows = 3 }) {
  return (
    <textarea
      value={value}
      onChange={e => onChange(e.target.value)}
      placeholder={placeholder}
      rows={rows}
      className="input resize-none"
    />
  );
}

function RadioGroup({ options, value, onChange }) {
  return (
    <div className="flex flex-wrap gap-3 pt-1">
      {options.map(opt => (
        <label key={opt} className="flex items-center gap-2 cursor-pointer group">
          <div
            onClick={() => onChange(opt)}
            className={`w-4 h-4 rounded-full border-2 flex items-center justify-center cursor-pointer transition-all ${
              value === opt ? 'border-brand-500 bg-brand-500' : 'border-slate-300 group-hover:border-brand-400'
            }`}
          >
            {value === opt && <div className="w-1.5 h-1.5 bg-white rounded-full" />}
          </div>
          <span className="text-sm text-slate-700 select-none" onClick={() => onChange(opt)}>{opt}</span>
        </label>
      ))}
    </div>
  );
}

function CheckboxGroup({ options, value = [], onChange }) {
  const toggle = (opt) => {
    if (value.includes(opt)) onChange(value.filter(v => v !== opt));
    else onChange([...value, opt]);
  };
  return (
    <div className="flex flex-wrap gap-3 pt-1">
      {options.map(opt => (
        <label key={opt} className="flex items-center gap-2 cursor-pointer group">
          <div
            onClick={() => toggle(opt)}
            className={`w-4 h-4 rounded border-2 flex items-center justify-center cursor-pointer transition-all ${
              value.includes(opt) ? 'border-brand-500 bg-brand-500' : 'border-slate-300 group-hover:border-brand-400'
            }`}
          >
            {value.includes(opt) && (
              <svg className="w-2.5 h-2.5 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
              </svg>
            )}
          </div>
          <span className="text-sm text-slate-700 select-none" onClick={() => toggle(opt)}>{opt}</span>
        </label>
      ))}
    </div>
  );
}

function SectionHead({ number, label }) {
  return (
    <div className="flex items-center gap-3 mb-5 pb-3 border-b-2 border-brand-100">
      <div className="w-7 h-7 rounded-full bg-brand-500 text-white text-xs font-bold flex items-center justify-center shrink-0">
        {number}
      </div>
      <h2 className="text-base font-bold text-brand-900">{label}</h2>
    </div>
  );
}

function SubHead({ label }) {
  return <p className="text-xs font-bold text-slate-500 uppercase tracking-wider mt-5 mb-3">{label}</p>;
}

// ─── Section renderers ────────────────────────────────────────────────────────
function Section1({ data, student, set }) {
  const f = (field) => (val) => set('s1', field, val);
  return (
    <div>
      <SectionHead number="1" label="Student Details" />

      <SubHead label="1.1 Basic Information" />
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <Field label="Student Full Name" required>
          <input className="input bg-slate-50" value={student?.name || ''} readOnly />
        </Field>
        <Field label="Student Email ID" required>
          <input className="input bg-slate-50" value={student?.email || ''} readOnly />
        </Field>
        <Field label="Student Mobile Number" required>
          <input className="input bg-slate-50" value={student?.phone || ''} readOnly />
        </Field>
        <Field label="Marital Status" required>
          <RadioGroup options={['Unmarried', 'Married', 'Divorced', 'Other']} value={data.maritalStatus} onChange={f('maritalStatus')} />
        </Field>
      </div>

      <SubHead label="1.2 Address Information" />
      <div className="grid grid-cols-1 gap-4">
        <Field label="Current Address (with Pin Code)" required>
          <TextArea value={data.currentAddress} onChange={f('currentAddress')} placeholder="Full address with pin code" />
        </Field>
        <Field label="Number of Years at Current Address" required>
          <TextInput value={data.currentAddressYears} onChange={f('currentAddressYears')} placeholder="e.g. 3" type="number" />
        </Field>
        <Field label="Permanent Address (with Pin Code)" required>
          <TextArea value={data.permanentAddress} onChange={f('permanentAddress')} placeholder="Full address with pin code" />
        </Field>
      </div>

      <SubHead label="1.3 Academic Information" />
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
        <Field label="10th Score (%)" required>
          <TextInput value={data.tenthScore} onChange={f('tenthScore')} placeholder="e.g. 85" type="number" />
        </Field>
        <Field label="12th Score (%)" required>
          <TextInput value={data.twelfthScore} onChange={f('twelfthScore')} placeholder="e.g. 78" type="number" />
        </Field>
        <Field label="Bachelor's Score (%)" required>
          <TextInput value={data.bachelorsScore} onChange={f('bachelorsScore')} placeholder="e.g. 72" type="number" />
        </Field>
        <Field label="Graduation Backlogs" required>
          <TextInput value={data.graduationBacklogs} onChange={f('graduationBacklogs')} placeholder="0" type="number" />
        </Field>
      </div>

      <SubHead label="1.4 Test Scores" />
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <Field label="Language Test + Score">
          <TextInput value={data.languageTest} onChange={f('languageTest')} placeholder="e.g. IELTS 7.0 / TOEFL 95 / Duolingo 120" />
        </Field>
        <Field label="GRE / GMAT Score">
          <TextInput value={data.greGmatScore} onChange={f('greGmatScore')} placeholder="e.g. GRE 320 / GMAT 680" />
        </Field>
      </div>

      <SubHead label="1.5 Reference Details (Non-Relative)" />
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <Field label="Reference Mobile Number" required>
          <TextInput value={data.refPhone} onChange={f('refPhone')} placeholder="+91 XXXXX XXXXX" />
        </Field>
        <Field label="Reference Address with Pin Code" required>
          <TextArea value={data.refAddress} onChange={f('refAddress')} placeholder="Full address with pin code" rows={2} />
        </Field>
      </div>

      <SubHead label="1.6 Work Profile" />
      <div className="grid grid-cols-1 gap-4">
        <Field label="Employment Type" required>
          <RadioGroup options={EMPLOYMENT_OPTS} value={data.employmentType} onChange={f('employmentType')} />
        </Field>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <Field label="Total Work Experience (Years)" required>
            <TextInput value={data.workExperience} onChange={f('workExperience')} placeholder="e.g. 2" type="number" />
          </Field>
          <Field label="Current Company Name & Address (with Pin Code)">
            <TextArea value={data.companyAddress} onChange={f('companyAddress')} placeholder="Company name + full address (Write NA if student)" rows={2} />
          </Field>
        </div>
      </div>
    </div>
  );
}

function Section2({ data, set }) {
  const f = (field) => (val) => set('s2', field, val);
  return (
    <div>
      <SectionHead number="2" label="University Details" />
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <Field label="Target Country" required>
          <TextInput value={data.targetCountry} onChange={f('targetCountry')} placeholder="e.g. Canada" />
        </Field>
        <Field label="University Name" required>
          <TextInput value={data.universityName} onChange={f('universityName')} placeholder="Full university name" />
        </Field>
        <Field label="Course Name" required>
          <TextInput value={data.courseName} onChange={f('courseName')} placeholder="e.g. MSc Data Science" />
        </Field>
        <Field label="Course Duration" required>
          <TextInput value={data.courseDuration} onChange={f('courseDuration')} placeholder="e.g. 2 Years" />
        </Field>
        <Field label="Loan Amount Required (In Lakhs)" required>
          <TextInput value={data.loanAmount} onChange={f('loanAmount')} placeholder="e.g. 40" type="number" />
        </Field>
      </div>
    </div>
  );
}

function Section3({ data, set }) {
  const f = (field) => (val) => set('s3', field, val);
  return (
    <div>
      <SectionHead number="3" label="Parents Details" />

      <SubHead label="Father's Details" />
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <Field label="Father's Name" required>
          <TextInput value={data.fatherName} onChange={f('fatherName')} placeholder="Full name" />
        </Field>
        <Field label="Father's Contact Number" required>
          <TextInput value={data.fatherPhone} onChange={f('fatherPhone')} placeholder="+91 XXXXX XXXXX" />
        </Field>
        <Field label="Father's Email ID" required>
          <TextInput value={data.fatherEmail} onChange={f('fatherEmail')} placeholder="email@example.com" type="email" />
        </Field>
      </div>

      <SubHead label="Mother's Details" />
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <Field label="Mother's Name" required>
          <TextInput value={data.motherName} onChange={f('motherName')} placeholder="Full name" />
        </Field>
        <Field label="Mother's Contact Number" required>
          <TextInput value={data.motherPhone} onChange={f('motherPhone')} placeholder="+91 XXXXX XXXXX" />
        </Field>
        <Field label="Mother's Email ID" required>
          <TextInput value={data.motherEmail} onChange={f('motherEmail')} placeholder="email@example.com" type="email" />
        </Field>
      </div>

      <SubHead label="Residence Address" />
      <Field label="Residence Address with Pin Code" required>
        <TextArea value={data.residenceAddress} onChange={f('residenceAddress')} placeholder="Full residence address with pin code" />
      </Field>
    </div>
  );
}

function Section4({ data, set }) {
  const f = (field) => (val) => set('s4', field, val);
  return (
    <div>
      <SectionHead number="4" label="Co-Applicant Details" />
      <div className="flex items-start gap-2 mb-5 p-3 bg-amber-50 border border-amber-200 rounded-xl text-xs text-amber-700">
        <svg className="w-4 h-4 mt-0.5 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v2m0 4h.01M12 2a10 10 0 100 20A10 10 0 0012 2z" />
        </svg>
        Banks prefer blood relatives (Father / Mother / Brother / Sister) as co-applicants.
      </div>

      <SubHead label="4.1 Basic Details" />
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <Field label="Co-Applicant Name" required>
          <TextInput value={data.name} onChange={f('name')} placeholder="Full name" />
        </Field>
        <Field label="Contact Number" required>
          <TextInput value={data.phone} onChange={f('phone')} placeholder="+91 XXXXX XXXXX" />
        </Field>
        <Field label="Email ID" required>
          <TextInput value={data.email} onChange={f('email')} placeholder="email@example.com" type="email" />
        </Field>
        <Field label="Relationship with Student" required>
          <TextInput value={data.relationship} onChange={f('relationship')} placeholder="e.g. Father" />
        </Field>
        <div className="sm:col-span-2">
          <Field label="Address with Pin Code" required>
            <TextArea value={data.address} onChange={f('address')} placeholder="Full address with pin code" />
          </Field>
        </div>
      </div>

      <SubHead label="4.2 Employment Details" />
      <div className="grid grid-cols-1 gap-4">
        <Field label="Occupation" required>
          <RadioGroup options={EMPLOYMENT_OPTS} value={data.occupation} onChange={f('occupation')} />
        </Field>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <Field label="Office Name & Address with Pin Code" required>
            <TextArea value={data.officeAddress} onChange={f('officeAddress')} placeholder="Office name + full address" rows={2} />
          </Field>
          <div className="space-y-4">
            <Field label="Years in Current Job / Business" required>
              <TextInput value={data.yearsInJob} onChange={f('yearsInJob')} placeholder="e.g. 5" type="number" />
            </Field>
            <Field label="Annual Income in Lakhs (as per ITR)" required>
              <TextInput value={data.annualIncome} onChange={f('annualIncome')} placeholder="e.g. 8.5" type="number" />
            </Field>
            <Field label="Monthly Existing EMIs (Total)" required>
              <TextInput value={data.monthlyEmis} onChange={f('monthlyEmis')} placeholder="e.g. 12000" type="number" />
            </Field>
          </div>
        </div>
      </div>

      <SubHead label="4.3 Family Details" />
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <Field label="Co-Applicant's Father's Name" required>
          <TextInput value={data.fatherName} onChange={f('fatherName')} placeholder="Full name" />
        </Field>
        <Field label="Co-Applicant's Mother's Name" required>
          <TextInput value={data.motherName} onChange={f('motherName')} placeholder="Full name" />
        </Field>
      </div>

      <SubHead label="4.4 References (Non-Relative)" />
      {[
        { prefix: 'ref1', label: 'Reference 1' },
        { prefix: 'ref2', label: 'Reference 2' },
      ].map(({ prefix, label }) => (
        <div key={prefix} className="mb-4">
          <p className="text-xs font-semibold text-slate-500 mb-3">{label}</p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <Field label="Name" required>
              <TextInput value={data[`${prefix}Name`]} onChange={f(`${prefix}Name`)} placeholder="Full name" />
            </Field>
            <Field label="Mobile Number" required>
              <TextInput value={data[`${prefix}Phone`]} onChange={f(`${prefix}Phone`)} placeholder="+91 XXXXX XXXXX" />
            </Field>
            <Field label="Email ID" required>
              <TextInput value={data[`${prefix}Email`]} onChange={f(`${prefix}Email`)} placeholder="email@example.com" type="email" />
            </Field>
            <Field label="Address with Pin Code" required>
              <TextInput value={data[`${prefix}Address`]} onChange={f(`${prefix}Address`)} placeholder="Address with pin code" />
            </Field>
          </div>
        </div>
      ))}

      <SubHead label="4.5 Additional Co-Applicant?" />
      <RadioGroup options={['Yes', 'No']} value={data.hasAdditional} onChange={f('hasAdditional')} />
    </div>
  );
}

function Section5({ data, set }) {
  const f = (field) => (val) => set('s5', field, val);
  return (
    <div>
      <SectionHead number="5" label="Miscellaneous Details" />
      <div className="grid grid-cols-1 gap-5">
        <Field label="Previously Applied to Any Bank? (Mention Bank Names)" required>
          <TextArea value={data.previousBanks} onChange={f('previousBanks')} placeholder="e.g. SBI, HDFC — or write 'No'" rows={2} />
        </Field>

        <Field label="Own House Available?" required>
          <RadioGroup options={['Yes', 'No']} value={data.ownHouse} onChange={f('ownHouse')} />
        </Field>

        <Field label="Ready to Provide Collateral (if required)?" required>
          <RadioGroup options={['Yes', 'No', 'Maybe']} value={data.collateral} onChange={f('collateral')} />
        </Field>

        {(data.collateral === 'Yes' || data.collateral === 'Maybe') && (
          <Field label="Accepted Collateral Types">
            <div className="mb-2 text-xs text-amber-700 bg-amber-50 px-3 py-2 rounded-lg border border-amber-100">
              Note: Agricultural land is not accepted as collateral.
            </div>
            <CheckboxGroup
              options={['Fixed Deposits', 'Residential Property (Flat / Plot)']}
              value={data.collateralTypes}
              onChange={f('collateralTypes')}
            />
          </Field>
        )}
      </div>
    </div>
  );
}

// ─── Main Page ────────────────────────────────────────────────────────────────
export default function ApplicationFormPage() {
  const { fbUser, student } = useAuth();
  const [form, setForm] = useState(EMPTY);
  const [activeIdx, setActiveIdx] = useState(0);
  const [formStatus, setFormStatus] = useState('draft'); // 'draft' | 'submitted'
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [submitMsg, setSubmitMsg] = useState('');
  const [declaration, setDeclaration] = useState(false);
  const [errors, setErrors] = useState('');

  // Load existing form
  useEffect(() => {
    getApplicationForm(fbUser)
      .then(res => {
        const f = res.data?.data?.form;
        if (f) {
          setForm({
            s1: { ...EMPTY.s1, ...f.s1 },
            s2: { ...EMPTY.s2, ...f.s2 },
            s3: { ...EMPTY.s3, ...f.s3 },
            s4: { ...EMPTY.s4, ...f.s4 },
            s5: { ...EMPTY.s5, ...f.s5 },
          });
          setFormStatus(f.status || 'draft');
          setDeclaration(f.declaration || false);
        }
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [fbUser]);

  const setField = useCallback((section, field, value) => {
    setForm(prev => ({ ...prev, [section]: { ...prev[section], [field]: value } }));
  }, []);

  const handleSave = async (submit = false) => {
    if (submit && !declaration) {
      setErrors('Please accept the declaration before submitting.');
      return;
    }
    setErrors('');
    setSaving(true);
    try {
      const res = await saveApplicationForm(fbUser, {
        ...form,
        status: submit ? 'submitted' : 'draft',
        declaration,
      });
      const saved = res.data?.data?.form;
      if (saved) setFormStatus(saved.status);
      setSubmitMsg(submit ? 'Application submitted successfully!' : 'Draft saved.');
      setTimeout(() => setSubmitMsg(''), 3000);
    } catch (err) {
      setErrors(err.response?.data?.message || 'Save failed. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  if (loading) return (
    <div className="flex flex-col items-center justify-center py-20 gap-3">
      <div className="w-8 h-8 border-2 border-brand-500 border-t-transparent rounded-full animate-spin" />
      <p className="text-sm text-slate-500">Loading your form…</p>
    </div>
  );

  const isSubmitted = formStatus === 'submitted';

  const sectionProps = [
    <Section1 data={form.s1} student={student} set={setField} />,
    <Section2 data={form.s2} set={setField} />,
    <Section3 data={form.s3} set={setField} />,
    <Section4 data={form.s4} set={setField} />,
    <Section5 data={form.s5} set={setField} />,
  ];

  return (
    <div className="px-4 sm:px-6 py-6 max-w-4xl mx-auto animate-fade-in space-y-4">
      {/* Page header */}
      <div className="mb-2">
        <h1 className="text-xl font-bold text-slate-800">Loan Application Form</h1>
        <p className="text-sm text-slate-500 mt-0.5">Fill in all sections and submit your application</p>
      </div>

      {/* Status banner */}
      {isSubmitted ? (
        <div className="flex items-center gap-3 bg-emerald-50 border border-emerald-200 rounded-xl px-4 py-3">
          <svg className="w-5 h-5 text-emerald-600 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          <div>
            <p className="text-sm font-semibold text-emerald-800">Application Submitted</p>
            <p className="text-xs text-emerald-600">Your education loan application form has been submitted. Our team will review it shortly.</p>
          </div>
        </div>
      ) : (
        <div className="flex items-center gap-3 bg-amber-50 border border-amber-200 rounded-xl px-4 py-3">
          <svg className="w-4 h-4 text-amber-600 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
          </svg>
          <p className="text-xs text-amber-700 font-medium">Draft — fill in all sections and submit. Your progress is saved automatically.</p>
        </div>
      )}

      {/* Section tabs */}
      <div className="card overflow-hidden">
        <div className="flex overflow-x-auto">
          {SECTIONS.map((sec, idx) => (
            <button
              key={sec.id}
              onClick={() => setActiveIdx(idx)}
              className={`flex-shrink-0 px-4 py-3 text-sm font-semibold border-b-2 transition-all duration-200 whitespace-nowrap ${
                activeIdx === idx
                  ? 'border-brand-500 text-brand-600 bg-brand-50'
                  : 'border-transparent text-slate-500 hover:text-slate-700 hover:bg-slate-50'
              }`}
            >
              <span className="hidden sm:inline">{sec.label}</span>
              <span className="sm:hidden">{sec.short}</span>
            </button>
          ))}
        </div>
      </div>

      {/* Section content */}
      <div className={`card p-5 sm:p-6 ${isSubmitted ? 'pointer-events-none opacity-70' : ''}`}>
        {sectionProps[activeIdx]}
      </div>

      {/* Declaration (last section) */}
      {activeIdx === 4 && !isSubmitted && (
        <div className="card p-5">
          <p className="text-sm font-bold text-slate-700 mb-3">Declaration</p>
          <label className="flex items-start gap-3 cursor-pointer">
            <div
              onClick={() => setDeclaration(d => !d)}
              className={`mt-0.5 w-4 h-4 rounded border-2 flex items-center justify-center shrink-0 transition-all ${
                declaration ? 'border-brand-500 bg-brand-500' : 'border-slate-300'
              }`}
            >
              {declaration && (
                <svg className="w-2.5 h-2.5 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                </svg>
              )}
            </div>
            <span className="text-sm text-slate-600 leading-relaxed">
              I hereby declare that all the information provided in this form is true and accurate to the best of my knowledge.
              I understand that any false information may lead to rejection of my loan application.
            </span>
          </label>
        </div>
      )}

      {/* Error */}
      {errors && (
        <div className="flex items-center gap-2 bg-red-50 border border-red-200 rounded-xl px-4 py-3 text-sm text-red-700">
          <svg className="w-4 h-4 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v2m0 4h.01M12 2a10 10 0 100 20A10 10 0 0012 2z" />
          </svg>
          {errors}
        </div>
      )}

      {/* Success msg */}
      {submitMsg && (
        <div className="flex items-center gap-2 bg-emerald-50 border border-emerald-200 rounded-xl px-4 py-3 text-sm text-emerald-700">
          <svg className="w-4 h-4 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
          </svg>
          {submitMsg}
        </div>
      )}

      {/* Action buttons */}
      {!isSubmitted && (
        <div className="flex flex-col sm:flex-row gap-3">
          {/* Prev / Next */}
          <div className="flex gap-2 flex-1">
            {activeIdx > 0 && (
              <button onClick={() => setActiveIdx(i => i - 1)} className="btn-secondary px-4 py-2 text-sm">
                ← Back
              </button>
            )}
            {activeIdx < SECTIONS.length - 1 && (
              <button onClick={() => { handleSave(false); setActiveIdx(i => i + 1); }} className="btn-secondary px-4 py-2 text-sm">
                Save & Next →
              </button>
            )}
          </div>

          {/* Save draft */}
          <button
            onClick={() => handleSave(false)}
            disabled={saving}
            className="btn-secondary px-5 py-2 text-sm"
          >
            {saving ? 'Saving…' : 'Save Draft'}
          </button>

          {/* Submit (last section only) */}
          {activeIdx === SECTIONS.length - 1 && (
            <button
              onClick={() => handleSave(true)}
              disabled={saving || !declaration}
              className="btn-primary px-6 py-2 text-sm"
            >
              {saving ? 'Submitting…' : 'Submit Application'}
            </button>
          )}
        </div>
      )}
    </div>
  );
}
